/**
 * SmartGoalWizard — Phase 2 of Care Plan & Goals v2.
 *
 * Layered on top of the existing GoalsTab create form. When the
 * `goals_smart_framework_enabled` feature flag is OFF the wizard renders
 * nothing and the legacy basic form continues to work. When ON the wizard
 * adds: SMART completeness scoring, measurement type picker, baseline /
 * target configuration, NDIS plan-goal linkage and support strategies.
 *
 * The wizard is a controlled component — the parent owns the form state
 * and submits the request. This keeps the legacy POST /goals payload
 * intact and just enriches it with v2 fields.
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { Sparkles, CheckCircle2, AlertCircle, X, Plus } from 'lucide-react';
import { AISuggestButton } from '@/components/ai/AISuggestButton';
import { AIDraftReviewDialog } from '@/components/ai/AIDraftReviewDialog';

const SMART_FIELDS = [
  { key: 'specific',    label: 'Specific',    hint: 'What exactly will be achieved?' },
  { key: 'measurable',  label: 'Measurable',  hint: 'How will we know it is achieved?' },
  { key: 'achievable',  label: 'Achievable',  hint: 'Is this realistic given current supports?' },
  { key: 'relevant',    label: 'Relevant',    hint: 'How does it tie to the participant\'s NDIS goals?' },
  { key: 'time_bound',  label: 'Time-bound',  hint: 'By when?' },
];

const MEASUREMENT_TYPES = [
  { value: 'PERCENT',   label: 'Percent (0-100%)' },
  { value: 'MILESTONE', label: 'Milestone checklist' },
  { value: 'CUSTOM',    label: 'Custom (unit, baseline → target)' },
  { value: 'GAS',       label: 'GAS (Phase 4)' },
  { value: 'COPM',      label: 'COPM (Phase 4)' },
];

export function SmartGoalWizard({ value, onChange, enabled, participantId, participantCtx }) {
  const [scoreData, setScoreData] = useState(null);
  const [scoring, setScoring] = useState(false);
  // Phase 5 — AI draft state. Two independent draft slots: one for the SMART
  // goal generator and one for support-strategy suggestions.
  const [goalDraft, setGoalDraft] = useState(null);
  const [stratDraft, setStratDraft] = useState(null);

  const smart = value.smart || {};
  const mtype = value.measurement_type || '';
  const cfg = value.measurement_config || {};
  const strategies = value.support_strategies || [];
  const [newStrategy, setNewStrategy] = useState('');

  // Pull score on smart change (debounced via useEffect dependency)
  useEffect(() => {
    if (!enabled) return undefined;
    const allEmpty = SMART_FIELDS.every(f => !(smart[f.key] || '').trim());
    if (allEmpty) {
      setScoreData(null);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      setScoring(true);
      api.post('/goals/validate-smart', { smart })
        .then(r => { if (!cancelled) setScoreData(r.data.data); })
        .catch(() => { if (!cancelled) setScoreData(null); })
        .finally(() => { if (!cancelled) setScoring(false); });
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [JSON.stringify(smart), enabled]);

  const setSmart = (k, v) => onChange({ ...value, smart: { ...smart, [k]: v } });
  const setCfg = (patch) => onChange({ ...value, measurement_config: { ...cfg, ...patch } });
  const setStrategies = (next) => onChange({ ...value, support_strategies: next });

  if (!enabled) return null;

  const milestones = cfg.milestones || [];

  return (
    <Card className="border-primary/20 bg-primary/[0.02]" data-testid="smart-goal-wizard">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <h4 className="font-semibold text-sm">SMART framework</h4>
          </div>
          <div className="flex items-center gap-2">
            <AISuggestButton
              feature="smart_goal"
              testid="ai-suggest-smart-goal"
              buildPayload={async () => ({
                participant_id: participantId,
                participant_ctx: participantCtx || {},
                user_brief: (value.title || '').trim() || 'Draft a SMART goal for this participant focused on their primary support needs.',
              })}
              onDraft={(env) => setGoalDraft(env)}
            />
            {scoreData && (
              <Badge
                data-testid="smart-completeness-badge"
                variant={scoreData.is_complete ? 'default' : 'outline'}
                className={scoreData.is_complete ? 'bg-emerald-600' : ''}
              >
                {scoring ? '…' : `${scoreData.completeness_score}% complete`}
              </Badge>
            )}
          </div>
        </div>

        {/* 5 SMART fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {SMART_FIELDS.map(f => (
            <div key={f.key} className="space-y-1">
              <Label className="text-xs flex items-center gap-1">
                {f.label}
                {scoreData?.filled_fields.includes(f.key) && <CheckCircle2 className="h-3 w-3 text-emerald-600" />}
                {scoreData?.too_short_fields.includes(f.key) && <AlertCircle className="h-3 w-3 text-amber-600" />}
              </Label>
              <Input
                value={smart[f.key] || ''}
                onChange={(e) => setSmart(f.key, e.target.value)}
                placeholder={f.hint}
                className="h-8 text-sm"
                data-testid={`smart-field-${f.key}`}
              />
            </div>
          ))}
        </div>

        {/* Measurement type + config */}
        <div className="border-t pt-3 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Measurement type</Label>
              <Select
                value={mtype}
                onValueChange={(v) => onChange({ ...value, measurement_type: v, measurement_config: {} })}
              >
                <SelectTrigger className="h-8 text-sm" data-testid="measurement-type-select">
                  <SelectValue placeholder="Choose a measurement type" />
                </SelectTrigger>
                <SelectContent>
                  {MEASUREMENT_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Funding category (optional)</Label>
              <Input
                value={value.funding_category || ''}
                onChange={(e) => onChange({ ...value, funding_category: e.target.value })}
                placeholder="e.g. Core - Daily Activities"
                className="h-8 text-sm"
                data-testid="funding-category-input"
              />
            </div>
          </div>

          {mtype === 'PERCENT' && (
            <div className="grid grid-cols-3 gap-3" data-testid="measurement-config-percent">
              <div className="space-y-1"><Label className="text-xs">Baseline %</Label><Input type="number" min="0" max="100" value={cfg.baseline ?? 0} onChange={(e) => setCfg({ baseline: parseInt(e.target.value || '0', 10) })} className="h-8 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs">Target %</Label><Input type="number" min="0" max="100" value={cfg.target ?? 100} onChange={(e) => setCfg({ target: parseInt(e.target.value || '0', 10) })} className="h-8 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs">Current %</Label><Input type="number" min="0" max="100" value={cfg.current ?? 0} onChange={(e) => setCfg({ current: parseInt(e.target.value || '0', 10) })} className="h-8 text-sm" /></div>
            </div>
          )}

          {mtype === 'CUSTOM' && (
            <div className="grid grid-cols-4 gap-3" data-testid="measurement-config-custom">
              <div className="space-y-1"><Label className="text-xs">Unit</Label><Input value={cfg.unit || ''} onChange={(e) => setCfg({ unit: e.target.value })} placeholder="trips / week" className="h-8 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs">Baseline</Label><Input type="number" value={cfg.baseline_value ?? 0} onChange={(e) => setCfg({ baseline_value: parseFloat(e.target.value || '0') })} className="h-8 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs">Target</Label><Input type="number" value={cfg.target_value ?? 0} onChange={(e) => setCfg({ target_value: parseFloat(e.target.value || '0') })} className="h-8 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs">Current</Label><Input type="number" value={cfg.current_value ?? 0} onChange={(e) => setCfg({ current_value: parseFloat(e.target.value || '0') })} className="h-8 text-sm" /></div>
            </div>
          )}

          {mtype === 'MILESTONE' && (
            <div className="space-y-2" data-testid="measurement-config-milestone">
              <Label className="text-xs">Milestones (will be tracked via /goals/{'{id}'}/measurement)</Label>
              {milestones.map((m, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input value={m.key || ''} placeholder="key (e.g. M1)" className="h-8 text-sm w-32"
                         onChange={(e) => {
                           const next = milestones.slice(); next[i] = { ...m, key: e.target.value };
                           setCfg({ milestones: next });
                         }} />
                  <Input value={m.label || ''} placeholder="Label (e.g. Apply for licence)" className="h-8 text-sm flex-1"
                         onChange={(e) => {
                           const next = milestones.slice(); next[i] = { ...m, label: e.target.value };
                           setCfg({ milestones: next });
                         }} />
                  <Button size="sm" variant="ghost" type="button"
                          onClick={() => setCfg({ milestones: milestones.filter((_, idx) => idx !== i) })}>
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
              <Button size="sm" variant="outline" type="button"
                      onClick={() => setCfg({ milestones: [...milestones, { key: '', label: '' }] })}>
                <Plus className="h-3 w-3 mr-1" /> Add milestone
              </Button>
            </div>
          )}

          {(mtype === 'GAS' || mtype === 'COPM') && (
            <p className="text-xs text-muted-foreground italic">
              {mtype} configuration finalises in Phase 4 (Outcomes &amp; Reporting). For now you can still
              record progress notes against this goal.
            </p>
          )}
        </div>

        {/* Support strategies */}
        <div className="border-t pt-3 space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Support strategies</Label>
            <AISuggestButton
              feature="support_strategies"
              testid="ai-suggest-support-strategies"
              buildPayload={async () => ({
                participant_id: participantId,
                participant_ctx: participantCtx || {},
                goal_ctx: {
                  title: value.title || '',
                  smart: value.smart || {},
                  measurement_type: value.measurement_type || '',
                  existing_strategies: value.support_strategies || [],
                },
              })}
              onDraft={(env) => setStratDraft(env)}
            />
          </div>
          <div className="flex flex-wrap gap-1">
            {strategies.map((s, i) => (
              <Badge key={i} variant="secondary" className="flex items-center gap-1">
                {s}
                <button type="button" onClick={() => setStrategies(strategies.filter((_, idx) => idx !== i))}>
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
          <div className="flex gap-2">
            <Input value={newStrategy} onChange={(e) => setNewStrategy(e.target.value)}
                   placeholder="e.g. Visual schedule rehearsal" className="h-8 text-sm"
                   data-testid="support-strategy-input"
                   onKeyDown={(e) => {
                     if (e.key === 'Enter') {
                       e.preventDefault();
                       if (newStrategy.trim()) { setStrategies([...strategies, newStrategy.trim()]); setNewStrategy(''); }
                     }
                   }} />
            <Button size="sm" variant="outline" type="button"
                    data-testid="support-strategy-add"
                    onClick={() => {
                      if (newStrategy.trim()) { setStrategies([...strategies, newStrategy.trim()]); setNewStrategy(''); }
                    }}>
              Add
            </Button>
          </div>
        </div>
      </CardContent>

      {/* Phase 5: AI draft review dialogs (mount when a draft exists). */}
      <AIDraftReviewDialog
        open={!!goalDraft}
        onOpenChange={(v) => !v && setGoalDraft(null)}
        draftEnvelope={goalDraft}
        participantId={participantId}
        targetEntity="goal"
        onAccept={(d) => {
          if (!d) return;
          // Patch the goal form with the accepted SMART content.
          const next = {
            ...value,
            title: d.title || value.title,
            smart: {
              specific: d.specific || value.smart?.specific || '',
              measurable: d.measurable || value.smart?.measurable || '',
              achievable: d.achievable || value.smart?.achievable || '',
              relevant: d.relevant || value.smart?.relevant || '',
              time_bound: d.time_bound || value.smart?.time_bound || '',
            },
            measurement_type: d.suggested_measurement_type || value.measurement_type || 'PERCENT',
          };
          onChange(next);
        }}
      />
      <AIDraftReviewDialog
        open={!!stratDraft}
        onOpenChange={(v) => !v && setStratDraft(null)}
        draftEnvelope={stratDraft}
        participantId={participantId}
        targetEntity="support_strategies"
        onAccept={(d) => {
          if (!d || !Array.isArray(d.strategies)) return;
          const titles = d.strategies.map(s => (s.title || '').trim()).filter(Boolean);
          const existing = value.support_strategies || [];
          const merged = [...existing];
          titles.forEach(t => { if (!merged.includes(t)) merged.push(t); });
          onChange({ ...value, support_strategies: merged });
        }}
      />
    </Card>
  );
}
